import { Plugin } from "vue";
import { isArray } from "@/utils/is";
import { FieldNamesProps } from "@/components/ProTable/interface";
import { Base64 } from "js-base64";
import { RouteRecordRaw } from "vue-router";
// import router from "@/routers";
// import { useGlobalStore } from "@/stores/modules/global";
// import { HOME_URL } from "@/config";
// const route = useRoute();

const mode = import.meta.env.VITE_ROUTER_MODE;

/**
 * @description 获取localStorage
 * @param {String} key Storage名称
 * @returns {String}
 */
export function localGet(key: string) {
  const value = window.localStorage.getItem(key);
  try {
    return JSON.parse(window.localStorage.getItem(key) as string);
  } catch (error) {
    return value;
  }
}

/**
 * @description 存储localStorage
 * @param {String} key Storage名称
 * @param {*} value Storage值
 * @returns {void}
 */
export function localSet(key: string, value: any) {
  window.localStorage.setItem(key, JSON.stringify(value));
}

/**
 * @description 清除localStorage
 * @param {String} key Storage名称
 * @returns {void}
 */
export function localRemove(key: string) {
  window.localStorage.removeItem(key);
}

export function keepSpecifiedlocal(keysToKeep: string) {
  const tempStorage = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (keysToKeep.includes(key)) {
      tempStorage[key] = localStorage.getItem(key);
    }
  }
  localStorage.clear();
  for (const key in tempStorage) {
    if (tempStorage.hasOwnProperty(key)) {
      localStorage.setItem(key, tempStorage[key]);
    }
  }
}

/**
 * @description 清除所有localStorage
 * @returns {void}
 */
export function localClear() {
  window.localStorage.clear();
}

/**
 * @description 判断数据类型
 * @param {*} val 需要判断类型的数据
 * @returns {String}
 */
export function isType(val: any) {
  if (val === null) return "null";
  if (typeof val !== "object") return typeof val;
  else return Object.prototype.toString.call(val).slice(8, -1).toLocaleLowerCase();
}

/**
 * @description 生成唯一 uuid
 * @returns {String}
 */
export function generateUUID() {
  let uuid = "";
  for (let i = 0; i < 32; i++) {
    let random = (Math.random() * 16) | 0;
    if (i === 8 || i === 12 || i === 16 || i === 20) uuid += "-";
    uuid += (i === 12 ? 4 : i === 16 ? (random & 3) | 8 : random).toString(16);
  }
  return uuid;
}

/**
 * 判断两个对象是否相同
 * @param {Object} a 要比较的对象一
 * @param {Object} b 要比较的对象二
 * @returns {Boolean} 相同返回 true，反之 false
 */
export function isObjectValueEqual(a: { [key: string]: any }, b: { [key: string]: any }) {
  if (!a || !b) return false;
  let aProps = Object.getOwnPropertyNames(a);
  let bProps = Object.getOwnPropertyNames(b);
  if (aProps.length != bProps.length) return false;
  for (let i = 0; i < aProps.length; i++) {
    let propName = aProps[i];
    let propA = a[propName];
    let propB = b[propName];
    if (!b.hasOwnProperty(propName)) return false;
    if (propA instanceof Object) {
      if (!isObjectValueEqual(propA, propB)) return false;
    } else if (propA !== propB) {
      return false;
    }
  }
  return true;
}

/**
 * @description 生成随机数
 * @param {Number} min 最小值
 * @param {Number} max 最大值
 * @returns {Number}
 */
export function randomNum(min: number, max: number): number {
  let num = Math.floor(Math.random() * (min - max) + max);
  return num;
}

/**
 * @description 获取当前时间对应的提示语
 * @returns {String}
 */
export function getTimeState() {
  let timeNow = new Date();
  let hours = timeNow.getHours();
  if (hours >= 6 && hours <= 9) return `早上好 ⛅`;
  if (hours >= 9 && hours <= 10) return `上午好 🌞`;
  if (hours >= 10 && hours <= 14) return `中午好 🌞`;
  if (hours >= 14 && hours <= 18) return `下午好 🌞`;
  if (hours >= 18 && hours <= 24) return `晚上好 🌛`;
  if (hours >= 0 && hours <= 6) return `凌晨好 🌛`;
}

/**
 * @description 获取浏览器默认语言
 * @returns {String}
 */
export function getBrowserLang() {
  let browserLang = navigator.language ? navigator.language : navigator.browserLanguage;
  let defaultBrowserLang = "";
  if (["cn", "zh", "zh-cn"].includes(browserLang.toLowerCase())) {
    defaultBrowserLang = "zh";
  } else {
    defaultBrowserLang = "en";
  }
  return defaultBrowserLang;
}

/**
 * @description 获取不同路由模式所对应的 url + params
 * @returns {String}
 */
export function getUrlWithParams() {
  const url = {
    hash: location.hash.substring(1),
    history: location.pathname + location.search
  };
  return url[mode];
}

/**
 * @description 使用递归扁平化菜单，方便添加动态路由
 * @param {Array} menuList 菜单列表
 * @returns {Array}
 */
export function getFlatMenuList(menuList: Menu.MenuOptions[]): Menu.MenuOptions[] {
  let newMenuList: Menu.MenuOptions[] = JSON.parse(JSON.stringify(menuList));
  return newMenuList.flatMap(item => [item, ...(item.children ? getFlatMenuList(item.children) : [])]);
}

/**
 * @description 使用递归过滤出需要渲染在左侧菜单的列表 (需剔除 isHide == true 的菜单)
 * @param {Array} menuList 菜单列表
 * @returns {Array}
 * */
export function getShowMenuList(menuList: Menu.MenuOptions[]) {
  let newMenuList: Menu.MenuOptions[] = JSON.parse(JSON.stringify(menuList));
  // console.log(newMenuList);
  return newMenuList.filter(item => {
    item.children?.length && (item.children = getShowMenuList(item.children));
    return !item.meta?.isHide;
  });
}

/**
 * @description 使用递归找出所有面包屑存储到 pinia/vuex 中
 * @param {Array} menuList 菜单列表
 * @param {Array} parent 父级菜单
 * @param {Object} result 处理后的结果
 * @returns {Object}
 */
export const getAllBreadcrumbList = (menuList: Menu.MenuOptions[], parent = [], result: { [key: string]: any } = {}) => {
  for (const item of menuList) {
    result[item.path] = [...parent, item];
    if (item.children) getAllBreadcrumbList(item.children, result[item.path], result);
  }
  return result;
};

/**
 * @description 使用递归处理路由菜单 path，生成一维数组 (第一版本地路由鉴权会用到，该函数暂未使用)
 * @param {Array} menuList 所有菜单列表
 * @param {Array} menuPathArr 菜单地址的一维数组 ['**','**']
 * @returns {Array}
 */
export function getMenuListPath(menuList: RouteRecordRaw[], menuPathArr: string[] = []): string[] {
  for (const item of menuList) {
    if (typeof item === "object" && item.path) menuPathArr.push(item.path);
    if (item.children?.length) getMenuListPath(item.children, menuPathArr);
  }
  return menuPathArr;
}

/**
 * @description 递归查询当前 path 所对应的菜单对象 (该函数暂未使用)
 * @param {Array} menuList 菜单列表
 * @param {String} path 当前访问地址
 * @returns {Object | null}
 */
export function findMenuByPath(menuList: Menu.MenuOptions[], path: string): Menu.MenuOptions | null {
  for (const item of menuList) {
    if (item.path === path) return item;
    if (item.children) {
      const res = findMenuByPath(item.children, path);
      if (res) return res;
    }
  }
  return null;
}

/**
 * @description 使用递归过滤需要缓存的菜单 name (该函数暂未使用)
 * @param {Array} menuList 所有菜单列表
 * @param {Array} keepAliveNameArr 缓存的菜单 name ['**','**']
 * @returns {Array}
 * */
export function getKeepAliveRouterName(menuList: Menu.MenuOptions[], keepAliveNameArr: string[] = []) {
  menuList.forEach(item => {
    item.meta.isKeepAlive && item.name && keepAliveNameArr.push(item.name);
    item.children?.length && getKeepAliveRouterName(item.children, keepAliveNameArr);
  });
  return keepAliveNameArr;
}

/**
 * @description 格式化表格单元格默认值 (el-table-column)
 * @param {Number} row 行
 * @param {Number} col 列
 * @param {*} callValue 当前单元格值
 * @returns {String}
 * */
export function formatTableColumn(row: number, col: number, callValue: any) {
  // 如果当前值为数组，使用 / 拼接（根据需求自定义）
  if (isArray(callValue)) return callValue.length ? callValue.join(" / ") : "--";
  return callValue ?? "--";
}

/**
 * @description 处理值无数据情况
 * @param {*} callValue 需要处理的值
 * @returns {String}
 * */
export function formatValue(callValue: any) {
  // 如果当前值为数组，使用 / 拼接（根据需求自定义）
  if (isArray(callValue)) return callValue.length ? callValue.join(" / ") : "--";
  return callValue ?? "--";
}

/**
 * @description 处理值无数据情况, 对象转换为字符串，用于详情展示
 * @param {*} callValue 需要处理的值
 * @returns {String}
 * */
export function formatValue2(callValue: any) {
  // 如果当前值为数组，转换为string
  if (isArray(callValue)) return callValue.length ? JSON.stringify(callValue) : "--";
  if (typeof callValue === "object") return callValue ? JSON.stringify(callValue) : "--";
  return callValue ?? "--";
}

/**
 * @description 处理 prop 为多级嵌套的情况，返回的数据 (列如: prop: user.name)
 * @param {Object} row 当前行数据
 * @param {String} prop 当前 prop
 * @returns {*}
 * */
export function handleRowAccordingToProp(row: { [key: string]: any }, prop: string) {
  if (!prop.includes(".")) return row[prop] ?? "--";
  prop.split(".").forEach(item => (row = row[item] ?? "--"));
  return row;
}

/**
 * @description 处理 prop，当 prop 为多级嵌套时 ==> 返回最后一级 prop
 * @param {String} prop 当前 prop
 * @returns {String}
 * */
export function handleProp(prop: string | null) {
  const propArr = prop.split(".");
  if (propArr.length == 1) return prop;
  return propArr[propArr.length - 1];
}

/**
 * @description 根据枚举列表查询当需要的数据（如果指定了 label 和 value 的 key值，会自动识别格式化）
 * @param {String} callValue 当前单元格值
 * @param {Array} enumData 字典列表
 * @param {Array} fieldNames label && value && children 的 key 值
 * @param {String} type 过滤类型（目前只有 tag）
 * @returns {String}
 * */
export function filterEnum(callValue: any, enumData?: any, fieldNames?: FieldNamesProps, type?: "tag"): string {
  const value = fieldNames?.value ?? "value";
  const label = fieldNames?.label ?? "label";
  const children = fieldNames?.children ?? "children";
  let filterData: { [key: string]: any } = {};
  // 判断 enumData 是否为数组
  if (Array.isArray(enumData)) filterData = findItemNested(enumData, callValue, value, children);
  // 判断是否输出的结果为 tag 类型
  if (type == "tag") {
    return filterData?.tagType ? filterData.tagType : "";
  } else {
    return filterData ? filterData[label] : "--";
  }
}

/**
 * @description 递归查找 callValue 对应的 enum 值
 * */
export function findItemNested(enumData: any, callValue: any, value: string, children: string) {
  return enumData.reduce((accumulator: any, current: any) => {
    if (accumulator) return accumulator;
    if (current[value] === callValue) return current;
    if (current[children]) return findItemNested(current[children], callValue, value, children);
  }, null);
}
/**
 * 复制文字内容到剪切板， 兼容Sarafi浏览器
 * @param {String} text 文本内容
 */
export function copyTextToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    // 使用 Clipboard API
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log("Text copied to clipboard");
      })
      .catch(err => {
        console.error("Failed to copy text: ", err);
      });
  } else {
    // 退回到更早的实现方式
    let textArea = document.createElement("textarea");
    textArea.value = text;
    // 避免滚动到文本区域
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.width = "2em";
    textArea.style.height = "2em";
    textArea.style.padding = "0";
    textArea.style.border = "none";
    textArea.style.outline = "none";
    textArea.style.boxShadow = "none";
    textArea.style.background = "transparent";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    setTimeout(function () {
      try {
        let successful = document.execCommand("copy");
        console.log("Fallback: Copying text command was " + (successful ? "successful" : "unsuccessful"));
      } catch (err) {
        console.error("Fallback: Oops, unable to copy", err);
      }

      document.body.removeChild(textArea);
    }, 200);
  }
}
export const copyText = text => {
  // 动态创建 textarea 标签
  const textarea: any = document.createElement("textarea");
  // 将该 textarea 设为 readonly 防止 iOS 下自动唤起键盘，同时将 textarea 移出可视区域
  textarea.readOnly = "readonly";
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  // 将要 copy 的值赋给 textarea 标签的 value 属性
  textarea.value = text;
  // 将 textarea 插入到 body 中
  document.body.appendChild(textarea);
  // 选中值并复制
  textarea.select();
  const result = document.execCommand("Copy");
  document.body.removeChild(textarea);
  return result;
};

/**
 *
 * @param component 需要注册的组件
 * @param alias 组件别名
 * @returns any
 */
export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any;
  comp.install = (app: any) => {
    app.component(comp.name || comp.displayName, component);
    if (alias) {
      app.config.globalProperties[alias] = component;
    }
  };
  return component as T & Plugin;
};

/**
 * @param str 需要转下划线的驼峰字符串
 * @returns 字符串下划线
 */
export const humpToUnderline = (str: string): string => {
  return str.replace(/([A-Z])/g, "-$1").toLowerCase();
};

/**
 * @param str 需要转驼峰的下划线字符串
 * @returns 字符串驼峰
 */
export const underlineToHump = (str: string): string => {
  if (!str) return "";
  return str.replace(/\-(\w)/g, (_, letter: string) => {
    return letter.toUpperCase();
  });
};

export const setCssVar = (prop: string, val: any, dom = document.documentElement) => {
  dom.style.setProperty(prop, val);
};

/**
 * 查找数组对象的某个下标
 * @param {Array} ary 查找的数组
 * @param {Functon} fn 判断的方法
 */
// eslint-disable-next-line
export const findIndex = <T = Recordable>(ary: Array<T>, fn: Fn): number => {
  if (ary.findIndex) {
    return ary.findIndex(fn);
  }
  let index = -1;
  ary.some((item: T, i: number, ary: Array<T>) => {
    const ret: T = fn(item, i, ary);
    if (ret) {
      index = i;
      return ret;
    }
  });
  return index;
};

export const trim = (str: string) => {
  return str.replace(/(^\s*)|(\s*$)/g, "");
};

/**
 * 时间格式化
 * @param {Date | number | string} time 需要转换的时间
 * @param {String} fmt 需要转换的格式 如 yyyy-MM-dd、yyyy-MM-dd HH:mm:ss
 */
export function formatTime(time: Date | number | string, fmt: string) {
  if (!time) return "";
  else {
    const date = new Date(time);
    const o: { [key: string]: number } = {
      "M+": date.getMonth() + 1,
      "d+": date.getDate(),
      "H+": date.getHours(),
      "m+": date.getMinutes(),
      "s+": date.getSeconds(),
      "q+": Math.floor((date.getMonth() + 3) / 3),
      S: date.getMilliseconds()
    };
    const yearMatch = fmt.match(/(y+)/);
    if (yearMatch) {
      const yearFormat = yearMatch[1];
      fmt = fmt.replace(yearFormat, (substring: string) => {
        return (date.getFullYear() + "").substring(4 - substring.length);
      });
    }
    for (const k in o) {
      const pattern = new RegExp("(" + k + ")");
      if (pattern.test(fmt)) {
        fmt = fmt.replace(pattern, (substring: string) => {
          return substring.length === 1 ? String(o[k]) : ("00" + o[k]).substring(("" + o[k]).length);
        });
      }
    }
    return fmt;
  }
}

/**
 * 生成随机字符串
 */
export function toAnyString() {
  const str: string = "xxxxx-xxxxx-4xxxx-yxxxx-xxxxx".replace(/[xy]/g, (c: string) => {
    const r: number = (Math.random() * 16) | 0;
    const v: number = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString();
  });
  return str;
}
// 转化时间格式
export function formattedDate(timestamp: string | any) {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hour = date.getHours().toString().padStart(2, "0");
  const minute = date.getMinutes().toString().padStart(2, "0");
  // const second = date.getSeconds().toString().padStart(2, "0");
  const formattedDate = `${year}-${month}-${day} ${hour}:${minute}`;
  return formattedDate;
}
// // 截取富文本图片文本
// export const Capture_images = (str: string) => {
//   const re1 = new RegExp("<.+?>", "g"); // 匹配html标签的正则表达式，"g"是搜索匹配多个符合的内容
//   const arrEntities = { lt: "<", gt: ">", nbsp: " ", amp: "&", quot: '"', ldquo: "“", mdash: "—", rdquo: "”" };
//   let taskdata = {
//     imgs: [],
//     title: str // 将原始字符串赋值给 title，以便之后可以修改它
//   };
//   // 使用 replace 方法提取 img 标签中的 src 属性值，并推入 imgs 数组
//   taskdata.title = taskdata.title.replace(/<img [^>]*src=['"]([^'"]+)[^>]*>/g, (match, capture) => {
//     taskdata.imgs.push(capture);
//     return ""; // 返回空字符串，以便在原始字符串中删除匹配的 img 标签
//   });
//   // 移除剩余的 HTML 标签，并替换 HTML 实体
//   taskdata.title = taskdata.title.replace(re1, "").replace(/&(lt|gt|nbsp|amp|quot|ldquo|mdash|rdquo);/gi, (all, t) => {
//     return arrEntities[t] || all; // 如果找不到实体，则返回原始匹配项
//   });
//   return taskdata; // 返回包含图像URLs和清理后的标题的对象
// };

// 计算文件大小
export const formatFileSize = (fileSize: number) => {
  let units: string[] = ["B", "KB", "MB", "GB", "TB"];
  let unitIndex: number = 0;

  while (fileSize >= 1000 && unitIndex < units.length - 1) {
    fileSize /= 1000;
    unitIndex++;
  }

  return fileSize.toFixed(2) + " " + units[unitIndex];
};

export const toIconify = (data: any) => {
  const upload_url = import.meta.env.VITE_FILE_UPLOAD_SERVER_URL;
  const preview_url = import.meta.env.VITE_FILE_VIEW_PREVIEW_URL;
  let originUrl = `${upload_url}/file/download/${data.id}`; //要预览文件的访问地址
  let previewUrl = originUrl + `?fullfilename=${data.sys_name}`;
  window.open(`${preview_url}/onlinePreview?url=` + encodeURIComponent(Base64.encode(previewUrl)));
};
// 判断是否为mobile端
export const isMobile = () => {
  if (
    window.navigator.userAgent.match(
      /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i
    )
  ) {
    return true; // 移动端
  } else {
    return false; // PC端
  }
};
// 获得url中的指定参数
export const getQueryVariable = (variable: string) => {
  let query = location.href;
  let rooms = query.split("?");
  for (let i = 0; i < rooms.length; i++) {
    let pair = rooms[i].split("=");
    if (pair[0] == variable) {
      return pair[1].split("&")[0];
    }
  }
  //或者拼接&后面的值
  let vars = query.split("&");
  for (let i = 0; i < vars.length; i++) {
    let pair = vars[i].split("=");
    if (pair[0] == variable) {
      return pair[1];
    }
  }
};
// 获取当前视口宽度
export const getViewportWidth = () => {
  return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
};
// 计算预计时间与当前时间时间差
export const getTimeDifference = (str: string, isshowhours: boolean = true, title: string = "") => {
  // 创建目标日期对象
  const targetDate = new Date(str); // 注意：日期字符串应该是 ISO 8601 格式
  // 获取当前日期对象
  const currentDate = new Date();
  // 计算时间差（毫秒）
  const diffInMilliseconds = targetDate.getTime() - currentDate.getTime();
  // 转换时间差为天和小时
  const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
  const diffInHours = Math.floor((diffInMilliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (diffInDays >= 0 || diffInHours >= 0) {
    return diffInDays + `天` + (isshowhours ? +diffInHours + "小时" : "");
  } else {
    return title ? title : "已超时且未约定";
  }
};
// 计算时间几点几天
export const calculateRemainingDays = (startDate: any = Date.now(), endTimeStamp: any, company: Boolean = false) => {
  const remainingMilliseconds = endTimeStamp - startDate; // 剩余毫秒数
  const days = remainingMilliseconds / (1000 * 60 * 60 * 24); // 剩余天数，保留小数
  const roundedDays = Math.round(days * 10) / 10; // 四舍五入到小数点后一位，以符合你的格式要求
  if (company) return roundedDays;
  else return roundedDays > 0 ? roundedDays + "天" : "已过";
};
// 计算时间xx小时前
export function calculateTimeDifference(targetTimestamp, estimatedTimestamp) {
  const differenceInMilliseconds = estimatedTimestamp.getTime() - new Date(targetTimestamp).getTime();
  const seconds = Math.floor(differenceInMilliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30); // 假设一个月有30天

  if (months >= 1) {
    return `${months}个月`;
  } else if (days >= 1) {
    return `${days}天`;
  } else if (hours >= 1) {
    return `${hours}小时`;
  } else if (minutes >= 1) {
    return `${minutes}分钟`;
  } else {
    return `${seconds}秒`;
  }
}
// 针对部分数组扁平化返回的是对象值
// export function flattenArrayObjects(arr: Array<any>) {
//   let result = [];

//   for (let i = 0; i < arr.length; i++) {
//     const item = arr[i];
//     result.push(item); // 将当前项添加到结果数组中

//     if (item.children && item.children.length > 0) {
//       // 如果当前项有子项，则递归调用flattenArrayObjects
//       const flattenedChildren = flattenArrayObjects(item.children);
//       result = result.concat(flattenedChildren); // 将子项扁平化后的结果合并到最终的结果数组中
//     }
//   }

//   return result;
// }
// 将文件按照时间顺序重新分组
export const groupedUploads = (files: any) => {
  const dateupload = {};
  files.forEach(upload => {
    const datePart = formatTime(upload.datetime, "yyyy-MM-dd"); // 提取日期部分
    if (!dateupload["datetime"]) {
      dateupload["files"] = [];
    }
    dateupload["datetime"] = datePart;
    dateupload["files"].push(upload);
  });
  return dateupload;
};

// export function transformObject(originalObj: Record<string, any>, objkey: string = "custom_value"): Record<string, any> {
//   const result: Record<string, any> = {};
//   let hasNumericKey = false; // 用来标记是否有数字键被处理

//   for (const key in originalObj) {
//     if (originalObj.hasOwnProperty(key)) {
//       if (/^\d+$/.test(key)) {
//         // 如果键是数字，特殊处理它
//         if (!hasNumericKey) {
//           result[objkey] = { source: originalObj[key] };
//           hasNumericKey = true; // 标记数字键已经被处理
//         }
//         // 注意：如果有多个数字键，这里只会保留第一个
//       } else {
//         // 如果键不是数字，直接添加到结果对象中
//         result[key] = originalObj[key];
//       }
//     }
//   }

//   // 如果原始对象中没有数字键，则 result 对象将只包含非数字键的属性
//   return result;
// }

export function transformObject(originalObj: Record<string, any>, objKey: string = "custom_value"): Record<string, any> {
  const result: Record<string, any> = {};
  const commentCustomId = originalObj.comment_custom_id; // 获取comment_custom_id的值

  for (const key in originalObj) {
    if (originalObj.hasOwnProperty(key)) {
      if (/^\d+$/.test(key) && key === String(commentCustomId)) {
        // 如果键是数字且与comment_custom_id相等
        result[objKey] = { source: originalObj[key] }; // 将值存储在result的objKey下
      } else if (/^\d+$/.test(key)) {
        // 如果键是数字但不是comment_custom_id，可以选择忽略或特殊处理但可以根据需要添加逻辑
      } else {
        // 如果键不是数字，直接添加到结果对象中
        result[key] = originalObj[key];
      }
    }
  }
  // 返回处理后的结果对象
  return result;
}

// 对比两个数组是否相等
export function arraysEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; ++i) {
    if (Array.isArray(a[i]) && Array.isArray(b[i])) {
      if (!arraysEqual(a[i], b[i])) return false;
    } else if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}
export function mergeArraysById(arr1, arr2, str) {
  // 使用对象来存储合并后的结果
  const merged = {};
  // 合并第一个数组
  arr1.forEach(item => {
    merged[item[str]] = { ...item }; // 使用展开运算符复制对象
  });
  // 合并第二个数组
  arr2.forEach(item => {
    if (merged[item[str]]) {
      // 如果ID已存在，则合并属性
      Object.keys(item).forEach(key => {
        if (merged[item[str]][key] === undefined) {
          // 如果合并的对象中没有这个属性，则直接添加
          merged[item[str]][key] = item[key];
        } else {
          // 处理属性冲突的逻辑（这里简单覆盖，可以根据需要调整）
          merged[item[str]][key] = item[key]; // 或者使用更复杂的合并逻辑
        }
      });
    } else {
      // 如果ID不存在，直接添加整个对象
      merged[item[str]] = { ...item };
    }
  });

  // 转换回数组（如果需要）
  return Object.values(merged);
}
/**
 * 防抖
 * @param {Function} func 回调方法
 * @param {String} time 执行时间
 */
// export function debounce(func: any, time: number) {
//   let timeout = null;
//   return function (...args: any) {
//     if (timeout !== null) clearTimeout(timeout);
//     timeout = setTimeout(() => {
//       func.apply(this, args);
//     }, time);
//   };
// }

export function removeQueryParameters(url, paramsToRemove) {
  // 分离URL的基础部分和查询字符串
  const [base, queryString] = url.split("?");

  // 如果没有查询字符串，则直接返回基础URL
  if (!queryString) {
    return base;
  }

  // 将查询字符串分割成参数数组
  const params = queryString
    .split("&")
    .map(param => param.split("="))
    .reduce((acc, [key, value]) => {
      acc[decodeURIComponent(key)] = decodeURIComponent(value);
      return acc;
    }, {});

  // 移除指定的参数
  for (const param of paramsToRemove) {
    delete params[param];
  }

  // 将剩余的参数转换回查询字符串格式
  const newQueryString = Object.keys(params)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join("&");

  // 如果还有剩余的查询参数，将它们与基础URL拼接起来
  if (newQueryString) {
    return `${base}?${newQueryString}`;
  }

  // 如果没有剩余的查询参数，只返回基础URL
  return base;
}

/**
 * 判断两个数组中execute数组中的值是否被包含在Executed数组中
 * @param {String} execute 需要执行判断的小数组
 * @param {String} Executed 被执行判断的大数组
 * @param {String} propName 唯一值key
 */
export function areValuesInB(execute: Array<any>, Executed: Array<any>, propName: string = "value") {
  return execute?.some(value => {
    return Executed?.some(obj => obj[propName] === value);
  });
}
/**
 * cs
 */
export function flattenArray(arr: Array<any>) {
  let meunlist = [];
  arr.forEach(item => {
    if (Array.isArray(item.children) && item.children.length > 0) {
      meunlist = meunlist.concat(flattenArray(item.children));
    } else {
      if (!item.meta.isHide) meunlist.push(item);
    }
  });
  return meunlist;
}

const crossDomain = import.meta.env.VITE_CROSS_DOMAIN;
// 获取Cookie值
export const getCookie = key => {
  // if (window.location.host.indexOf("192.168") != -1) return "";
  document.domain = crossDomain;
  // document.cookie拿取的是所有cookie字符串
  let strCookie = document.cookie;
  // 将所有cookie分割成数组形式
  let arrCookie = strCookie.split("; ");
  // 遍历数组，找到你想拿取的那一个
  for (let i = 0; i < arrCookie.length; i++) {
    let arr = arrCookie[i].split("=");
    if (key == arr[0]) {
      return arr[1];
    }
  }
  return "";
};

export const removeCookie = (key, value) => {
  document.cookie = `${key}=${value};path=/;domain=${crossDomain}`;
};

export const downloadFile = (url, fileName) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  // link.target = "_blank"; // 可选，如果希望在新窗口中下载文件，请取消注释此行
  link.click();
};

// 验证用户是否登录注册
// export const verifyLogin = () => {
//   const dev_user = window.localStorage.getItem("dev-user") ? JSON.parse(window.localStorage.getItem("dev-user")) : {};
//   if (!dev_user?.token || !dev_user?.userInfo?.telephone) {
//     console.log(route);
//     const current = router.options.history?.state?.current || HOME_URL;
//     useGlobalStore().setGlobalState("redirectGo", current as string);
//     router.push("/verifyLogin");
//   }
// };
// 截取富文本图片文本

export const Capture_images = (str: string) => {
  // const re1 = new RegExp("<.+?>", "g"); // 匹配html标签的正则表达式，"g"是搜索匹配多个符合的内容
  const arrEntities = { lt: "<", gt: ">", nbsp: " ", amp: "&", quot: '"', ldquo: "“", mdash: "—", rdquo: "”" };
  let taskdata = {
    imgs: [],
    title: str // 将原始字符串赋值给 title，以便之后可以修改它
  };
  // 使用 replace 方法提取 img 标签中的 src 属性值，并推入 imgs 数组
  taskdata.title = taskdata?.title
    ? taskdata?.title?.replace(/<img [^>]*src=['"]([^'"]+)[^>]*>/g, (match, capture) => {
        taskdata.imgs.push(capture);
        return ""; // 返回空字符串，以便在原始字符串中删除匹配的 img 标签
      })
    : null;
  // 移除剩余的 HTML 标签，并替换 HTML 实体
  taskdata.title = cleanTextWithLineBreaks(taskdata?.title).replace(/&(lt|gt|nbsp|amp|quot|ldquo|mdash|rdquo);/gi, (all, t) => {
    return arrEntities[t] || all; // 如果找不到实体，则返回原始匹配项
  });
  return taskdata; // 返回包含图像URLs和清理后的标题的对象
};

// 特殊文本换行处理
export function cleanTextWithLineBreaks(html) {
  // 1. 处理 <br> 标签（自闭合标签兼容）
  let text = html.replace(/<br\s*\/?>/gi, "\n");

  // 2. 处理段落 </p> 标签（转换为两个换行）
  text = text.replace(/<\/p>/gi, "\n\n");

  // 3. 移除其他 HTML 标签
  text = text.replace(/<[^>]+>/g, "");

  // 4. 合并连续换行（可选）
  text = text.replace(/\n+/g, "\n");

  // 5. 替换 HTML 实体（如 &lt; 转 <）
  const entities = { lt: "<", gt: ">", nbsp: " " /* 其他实体 */ };
  text = text.replace(/&([a-z]+);/gi, (match, key) => entities[key] || match);

  return text.trim();
}

// 随机编码
export const generateUuid = () => {
  let d = new Date().getTime();
  let uuid = "xxxxx-4xxxx".replace(/[xy]/g, function (c) {
    let r = (d + Math.random() * 16) % 10 | 0;
    d = Math.floor(d / 16);
    return (c == "x" ? r : (r & 0x3) | 0x10).toString(16);
  });
  return uuid;
};

export const setCookie = (key, value) => {
  document.domain = crossDomain;
  document.cookie = `${key}=${value};path=/;domain=${crossDomain}`;
};
